import { useState, useCallback } from 'react';
import * as api from '../api/client';

/**
 * Tool name → friendly status line shown while the tool is running.
 * Falls back to the label sent in the tool_start event.
 */
function toolStatusText(label, args) {
  if (args?.query)  return `${label}: "${args.query}"`;
  if (args?.ticker) return `${label}: ${args.ticker}`;
  if (args?.url)    return `${label}: ${args.url}`;
  return label;
}

/**
 * Convert a File object to a plain base64 string (no data-URI prefix).
 */
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Encapsulates the send-message flow with full SSE streaming.
 *
 * Event protocol (from /chat/stream):
 *   {type: "tool_start",  tool, label, args}   — a tool is being called
 *   {type: "tool_done",   tool, ok}             — tool finished
 *   {type: "text_delta",  delta}                — one streamed token
 *   {type: "done",        thread_id, saved_to_kb}
 *   {type: "error",       message}
 */
export function useChatSend({
  activeId,
  setActiveId,
  messages,
  addMessage,
  appendToLastMessage,
  updateLastMessage,
  markLastExchangePersistent,
  loadPrompts,
  loadDocuments,
  refreshSessions,
  activeSystemPrompt,
  activeToolConfig,
  saveToKbByDefault,
}) {
  const [loading, setLoading]       = useState(false);
  const [toolStatus, setToolStatus] = useState(null);

  const sendMessage = useCallback(async (text, pendingFile = null) => {
    if ((!text.trim() && !pendingFile) || loading) return;

    setLoading(true);
    setToolStatus(null);

    let threadId       = activeId;
    let savedToKb      = false;
    let hasTextStarted = false;
    let hasErrored     = false;

    try {
      // Build attachments array if a file was provided
      let attachments = null;
      if (pendingFile) {
        const data = await fileToBase64(pendingFile);
        attachments = [{
          filename:   pendingFile.name,
          media_type: pendingFile.type,
          data,
        }];
      }

      // Build context BEFORE addMessage — state is still fresh here.
      // Manually append the current message so it's included in context
      // with its attachments, since React state won't update until re-render.
      const contextWithCurrent = [
        ...messages,
        {
          role:        'user',
          content:     text,
          attachments: attachments || null,
        },
      ];

      addMessage({
        role:        'user',
        content:     text,
        attachment:  pendingFile ? pendingFile.name : null,
        attachments: attachments || null,
      });

      // Placeholder assistant bubble — updated as events arrive
      addMessage({ role: 'assistant', content: '' });

      const stream = await api.streamMessage(
        text,
        activeId,
        saveToKbByDefault,
        contextWithCurrent,   // ← manually built context, not stale messages
        activeSystemPrompt,
        activeToolConfig,
        attachments,
      );

      const reader  = stream.getReader();
      const decoder = new TextDecoder();
      let buffer    = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE lines are separated by \n\n; a single read may contain multiple
        const parts = buffer.split('\n\n');
        buffer = parts.pop(); // last part may be incomplete — keep in buffer

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;

          let event;
          try {
            event = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }

          switch (event.type) {

            case 'thinking': {
              updateLastMessage({ content: '', toolStatus: 'Thinking…' });
              break;
            }

            case 'tool_start': {
              const status = toolStatusText(event.label, event.args);
              setToolStatus(status);
              updateLastMessage({ content: '', toolStatus: status });
              break;
            }

            case 'tool_done': {
              // Keep status visible until text starts arriving
              break;
            }

            case 'text_delta': {
              if (!hasTextStarted && !hasErrored) {
                hasTextStarted = true;
                setToolStatus(null);
                updateLastMessage({ content: event.delta, toolStatus: null });
              } else {
                appendToLastMessage(event.delta);
              }
              break;
            }

            case 'done': {
              threadId  = event.thread_id;
              savedToKb = event.saved_to_kb;
              break;
            }

            case 'error': {
              hasErrored = true;
              updateLastMessage({
                role:       'error',
                content:    event.message || 'Something went wrong. Please try again.',
                toolStatus: null,
              });
              break;
            }

            default:
              break;
          }
        }
      }

      // If the assistant bubble is still empty after the stream ends
      // (e.g. all events were tool events and text never arrived),
      // replace it with a generic error rather than leaving a blank bubble.
      if (!hasTextStarted && !hasErrored) {
        updateLastMessage({
          role:    'error',
          content: 'No response received. Please try again.',
        });
      }

      if (savedToKb) {
        markLastExchangePersistent();
      }

      if (!activeId && threadId) {
        setActiveId(threadId);
        loadPrompts(threadId);
        loadDocuments(threadId);
        refreshSessions();
      }

    } catch (err) {
      updateLastMessage({
        role:    'error',
        content: 'Something went wrong. Please try again later.',
      });
    } finally {
      setLoading(false);
      setToolStatus(null);
    }
  }, [
    loading, activeId, messages, saveToKbByDefault,
    activeSystemPrompt, activeToolConfig,
    addMessage, appendToLastMessage, updateLastMessage,
    markLastExchangePersistent,
    setActiveId, loadPrompts, loadDocuments, refreshSessions,
  ]);

  return { loading, toolStatus, sendMessage };
}